import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { feedNew, onFeedNew } from '../lib/feed-flash';
import { useTodayWaiting } from '../lib/today-waiting';

/**
 * The phone frame: mint pegboard on top, cream wall, honey desk at the bottom,
 * and the four tabs when asked. `back` puts a way home in the header for the
 * screens without tabs (an exercise opened from a link, the reward), so no
 * deep link is a dead end.
 *
 * `onBack` is the same arrow with a screen's own answer behind it, for the ones
 * that have something to ask before they let go of it — an exercise underway
 * has words in it that leaving would drop.
 */
export function Frame({ left, right, tabs, back, onBack, theme, children }: { left?: ReactNode; right?: ReactNode; tabs?: boolean; back?: boolean; onBack?: () => void; theme?: string; children: ReactNode }) {
  return (
    <div className={`phone ${theme ?? ''}`}>
      <header className="pegboard">
        <div className="row">{onBack ? <button type="button" className="back" aria-label="Back" onClick={onBack}>‹</button> : back && <Link to="/" className="back" aria-label="Today">‹</Link>}{left}</div>
        <div className="row">{right ?? <span className="wordmark">Kindling</span>}</div>
      </header>
      <main className="main">{children}</main>
      {tabs ? (
        <nav className="tabs" aria-label="Sections">
          <NavLink to="/" end className={({ isActive }) => `tab ${isActive ? 'on' : ''}`}><span className="tab-ico">⌂</span>Today<TodayDot /></NavLink>
          <NavLink to="/path" className={({ isActive }) => `tab ${isActive ? 'on' : ''}`}><span className="tab-ico">⋯</span>Road</NavLink>
          <NavLink to="/feed" className={({ isActive }) => `tab ${isActive ? 'on' : ''}`}><span className="tab-ico">≡</span>Feed<FeedFlash /></NavLink>
          <NavLink to="/friends" className={({ isActive }) => `tab ${isActive ? 'on' : ''}`}><span className="tab-ico">☺</span>Friends</NavLink>
        </nav>
      ) : <div className="desk" />}
    </div>
  );
}

/** Something dry and not yet turned over waits on Today (lib/today-waiting.ts); a plain dot, never the picture. */
function TodayDot() {
  // Home shows the print itself, so there is nothing to knock about there
  const n = useTodayWaiting(useLocation().pathname !== '/');
  if (!n) return null;
  return <span className="tab-dot" aria-label={n === 1 ? 'Something is waiting on Today' : `${n} things are waiting on Today`}>{n > 1 ? n : ''}</span>;
}

/** The picture Kraben just posted, as a tiny badge on the Feed tab, until you go and look (lib/feed-flash.ts). */
function FeedFlash() {
  const [n, setN] = useState(feedNew);
  useEffect(() => onFeedNew(() => setN(feedNew())), []);
  if (!n) return null;
  return <span className="tab-new" aria-label="New in your feed"><img src={n.url} alt="" /></span>;
}
